/**
 * ParticipantsSubpage — MED-1009 / E4.0e AC5–AC8
 *
 * Read-only reference view for the Session Leaders tab, organized into sections
 * by group (mirroring the Schedule grid's group rows). Each participant row
 * surfaces the intake-derived rotation rationale the E4.0c algorithm used:
 *
 *   • IvsE average + prep-preference average (the primary sort signals)
 *   • Topic excitement — session numbers the participant flagged as "most eager
 *     to enhance", compact as `S#` with a hover tooltip expanding to
 *     `S# · topic name` (AC5)
 *   • Prior experience — Yes/No, with a tooltip listing the specific selections
 *   • Availability notes — free-text intake note
 *   • Sessions assigned — which sessions the participant leads, as `S#` (AC6)
 *
 * "Sessions assigned" comes from the FE's phase-aware rotation state (proposal
 * map or finalized grid), passed in by the container, so it always matches the
 * Schedule grid (IV2). Once a rotation has been generated (proposal OR
 * finalized), any participant with zero assigned sessions is flagged ⚠ (AC7);
 * the flag is suppressed in the empty/pre-generation state.
 *
 * This subpage has NO edit affordances — all assignment happens on Schedule
 * (AC8 / IV6).
 */
import { Alert, Group, Stack, Table, Text, ThemeIcon, Title, Tooltip } from '@mantine/core';
import { AlertCircle, AlertTriangle } from 'react-feather';
import type { ParticipantRationaleGroup } from '../../../../services/api/sessionLeaders';
import { compactSessions, formatScore } from './rationaleFormat';

interface ParticipantsSubpageProps {
  groups: ParticipantRationaleGroup[];
  /** Assigned session numbers per participant user_id (phase-aware, from the container). */
  assignedSessionNumbersByUserId: Map<string, number[]>;
  /** True once a rotation has been generated (proposal or finalized) — gates the ⚠ flag. */
  rotationGenerated: boolean;
}

export function ParticipantsSubpage({
  groups,
  assignedSessionNumbersByUserId,
  rotationGenerated,
}: ParticipantsSubpageProps) {
  if (groups.length === 0) {
    return (
      <Alert color="blue" icon={<AlertCircle size={16} />}>
        No groups with participants yet. Add participants to groups to see their rotation rationale.
      </Alert>
    );
  }

  return (
    <Stack gap="xl">
      {groups.map(group => (
        <Stack key={group.group_id} gap="xs">
          <Group gap="xs" align="baseline">
            <Title order={5}>{group.group_name}</Title>
            <Text size="xs" c="dimmed">
              · {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
            </Text>
          </Group>

          {group.participants.length === 0 ? (
            <Text size="sm" c="dimmed">
              No participants in this group.
            </Text>
          ) : (
            <Table withTableBorder withColumnBorders highlightOnHover striped="even" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Participant</Table.Th>
                  <Table.Th style={{ width: 80 }}>IvsE</Table.Th>
                  <Table.Th style={{ width: 80 }}>Prep</Table.Th>
                  <Table.Th style={{ width: 120 }}>Topic exc.</Table.Th>
                  <Table.Th style={{ width: 110 }}>Prior exp.</Table.Th>
                  <Table.Th>Availability notes</Table.Th>
                  <Table.Th style={{ width: 150 }}>Sessions assigned</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {group.participants.map(p => {
                  const assigned = assignedSessionNumbersByUserId.get(p.user_id) ?? [];
                  const hasAssignment = assigned.length > 0;
                  const showFlag = rotationGenerated && !hasAssignment;
                  return (
                    <Table.Tr key={p.user_id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {p.first_name} {p.last_name}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatScore(p.ivse_average)}</Table.Td>
                      <Table.Td>{formatScore(p.prep_preference_average)}</Table.Td>
                      <Table.Td>
                        {p.topic_excitement.length > 0 ? (
                          <Tooltip
                            multiline
                            withArrow
                            label={
                              <Stack gap={2}>
                                {p.topic_excitement.map(t => (
                                  <Text key={t.session_number} size="xs">
                                    S{t.session_number} · {t.topic_name}
                                  </Text>
                                ))}
                              </Stack>
                            }
                          >
                            <Text size="sm" style={{ cursor: 'help' }}>
                              {compactSessions(p.topic_excitement.map(t => t.session_number))}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {p.prior_experience.choices.length > 0 ? (
                          <Tooltip
                            multiline
                            withArrow
                            label={
                              <Stack gap={2}>
                                {p.prior_experience.choices.map((c, i) => (
                                  <Text key={i} size="xs">
                                    {c}
                                  </Text>
                                ))}
                              </Stack>
                            }
                          >
                            <Text size="sm" style={{ cursor: 'help' }}>
                              {p.prior_experience.has_experience ? 'Yes' : 'No'}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="sm">{p.prior_experience.has_experience ? 'Yes' : 'No'}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {p.availability_notes ? (
                          <Text size="sm">{p.availability_notes}</Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {hasAssignment ? (
                          <Text size="sm">{compactSessions(assigned)}</Text>
                        ) : showFlag ? (
                          <Group gap={4} wrap="nowrap">
                            <ThemeIcon color="warningAmber" variant="light" size="sm" radius="xl">
                              <AlertTriangle size={12} />
                            </ThemeIcon>
                            <Text size="sm" c="warningAmber" fw={500}>
                              none
                            </Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      ))}

      <Text size="xs" c="dimmed">
        Read-only · hover Topic exc. for &ldquo;S# · topic&rdquo; · hover Prior exp. for specific
        selections · <AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> no sessions
        assigned · IvsE scale -2 (introvert) to +2 (extrovert)
      </Text>
    </Stack>
  );
}

export default ParticipantsSubpage;
